import { useCallback, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Layers3, LayoutGrid } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useExtracted } from "next-intl";
import { auth as api } from "@/lib/api/auth";
import { ApiError, AuthError } from "@/lib/api/transport";
import { hasActiveAccess } from "@/lib/auth/tier";
import { manifoldExampleReturnTo } from "@/lib/manifold/examples";
import {
  clearManifoldExplore,
  readManifoldExploreReturnTo,
} from "@/lib/manifold/explore-intent";
import { PUBLIC_STRATEGY_IMPORT_PATH } from "@/lib/public-strategy";
import { WORKSPACE_ROUTES } from "@/lib/workspace-routes";
import { useAuth } from "@/hooks/useAuth";
import { useBotProtection } from "@/hooks/useBotProtection";
import { PinInput } from "@/components/auth/PinInput";
import { RecoveryForm } from "@/components/auth/RecoveryForm";
import Link from "@/components/compat/Link";
import { UnauthShell } from "@/components/layout/UnauthShell";
import { LogoIcon } from "@/components/ui/logo-icon";
import { Logotype } from "@/components/ui/logotype";
import type { SessionPayload } from "@/types/api";
import { V3_ROUTES } from "@/features/v3/routes";

type LoginErrorKind =
  | "account_expired"
  | "invalid_pin"
  | "rate_limited"
  | "error";

// Registration entry and PIN login for unauthenticated visitors.
//
// Submit flow:
// 1. POST `/api/v1/login` directly (bypassing useAuth.login) so the
//    success animation can hand off to a branded welcome decision card.
// 2. Once the user picks Simulator or their original destination, call
//    `setSession(payload)` — this flips `user.id` and the `AuthScopedTree`
//    (App.tsx) atomically remounts every provider below the auth boundary,
//    and we navigate to the destination in the same tick.
export function Login() {
  const { setSession, status } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const location = useLocation();
  // Only this explicit internal destination can skip the normal welcome screen.
  const resumePublicStrategy =
    search.get("returnTo") === PUBLIC_STRATEGY_IMPORT_PATH ||
    location.state?.from === PUBLIC_STRATEGY_IMPORT_PATH;
  const requestedReturn = search.get("returnTo") ?? location.state?.from;
  const resumeManifold =
    manifoldExampleReturnTo(requestedReturn ?? null) ??
    (requestedReturn === "/workspace/workbench/import-example"
      ? WORKSPACE_ROUTES.workbenchModel
      : !requestedReturn
        ? readManifoldExploreReturnTo()
        : null);
  const te = useExtracted();
  const [errorKind, setErrorKind] = useState<LoginErrorKind | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showRecover, setShowRecover] = useState(false);
  const [recoverEmail, setRecoverEmail] = useState("");
  const [recoverSent, setRecoverSent] = useState(false);
  const [recoverLoading, setRecoverLoading] = useState(false);
  const [recoverError, setRecoverError] = useState<
    "rate_limited" | "failed" | null
  >(null);
  const { verify, challenge, cancel: cancelVerification } = useBotProtection();
  const [pendingSession, setPendingSession] = useState<SessionPayload | null>(
    null,
  );
  const skipEntry = status === "authenticated";

  const submit = useCallback(
    async (pin: string) => {
      setLoading(true);
      setErrorKind(null);
      let payload;
      try {
        payload = await api.login(pin);
      } catch (err) {
        if (
          err instanceof ApiError &&
          err.status === 403 &&
          typeof err.body === "object" &&
          err.body !== null &&
          "error" in err.body &&
          err.body.error === "account_expired"
        ) {
          setErrorKind("account_expired");
        } else if (err instanceof AuthError) {
          setErrorKind("invalid_pin");
        } else if (err instanceof ApiError && err.status === 429) {
          setErrorKind("rate_limited");
        } else {
          setErrorKind("error");
        }
        setResetKey((k) => k + 1);
        setLoading(false);
        return;
      }

      // Pre-seed the locale localStorage so I18nProvider's initial
      // state (read on the AuthScopedTree remount) lands on user B's
      // locale without a one-frame flash through user A's cached
      // value. Theme persistence is owned reactively by useThemeClass.
      if (payload.user.locale) {
        localStorage.setItem("locale", payload.user.locale);
      }

      if (resumeManifold) {
        clearManifoldExplore();
        setSession(payload);
        navigate(resumeManifold, { replace: true });
        return;
      }
      if (resumePublicStrategy) {
        setSession(payload);
        navigate(PUBLIC_STRATEGY_IMPORT_PATH, { replace: true });
        return;
      }
      setPendingSession(payload);
      setLoading(false);
    },
    [resumePublicStrategy, resumeManifold, setSession, navigate],
  );

  const completeLogin = useCallback(
    (target: "general" | "flagship") => {
      if (!pendingSession) return;
      setSession(pendingSession);
      const canEnterFlagship =
        pendingSession.user.admin ||
        hasActiveAccess({
          tier: pendingSession.user.tier,
          expiresAt: pendingSession.user.expires_at,
        });
      navigate(
        target === "general"
          ? "/workspace/general/home"
          : canEnterFlagship
            ? V3_ROUTES.home
            : "/purchase?plan=flagship",
        { replace: true },
      );
    },
    [navigate, pendingSession, setSession],
  );

  const handleRecover = useCallback(async () => {
    if (!recoverEmail || recoverLoading) return;
    setRecoverLoading(true);
    setRecoverError(null);
    try {
      const token = await verify("pin_recovery");
      await api.recoverPin(recoverEmail, token);
      setRecoverSent(true);
    } catch (error) {
      setRecoverError(
        error instanceof ApiError && error.status === 429
          ? "rate_limited"
          : "failed",
      );
    } finally {
      setRecoverLoading(false);
    }
  }, [recoverEmail, recoverLoading, verify]);

  const errorMessage =
    errorKind === "account_expired"
      ? te("Your access has ended. Purchase access to continue.")
      : errorKind === "rate_limited"
        ? te("Too many attempts. Wait a minute and try again.")
        : errorKind === "error"
          ? te("Login failed. Try again.")
          : null;

  return (
    <UnauthShell hideLogotype mode={pendingSession ? "scroll" : "center"}>
      <div className="relative z-10 flex w-full flex-col items-center">
        <div
          className="pointer-events-none absolute h-150 w-150 rounded-full opacity-[0.08]"
          style={{
            background:
              "radial-gradient(circle, var(--interactive) 0%, transparent 60%)",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
        <div
          className="pointer-events-none absolute h-100 w-100 rounded-full opacity-[0.06]"
          style={{
            background:
              "radial-gradient(circle, var(--pink) 0%, transparent 60%)",
            top: "50%",
            left: "55%",
            transform: "translate(-50%, -50%)",
          }}
        />

        <AnimatePresence mode="wait">
          {pendingSession ? (
            <LoginWorkspaceGateway
              key="workspace-gateway"
              onEnter={completeLogin}
              canEnterFlagship={
                pendingSession.user.admin ||
                hasActiveAccess({
                  tier: pendingSession.user.tier,
                  expiresAt: pendingSession.user.expires_at,
                })
              }
            />
          ) : (
            <motion.div
              key="login-form"
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              className="flex w-full flex-col items-center gap-10"
              exit={{
                opacity: 0,
                y: -18,
                filter: "blur(6px)",
                transition: { duration: 0.26, ease: "easeIn" },
              }}
              initial={
                skipEntry ? false : { opacity: 0, y: 12, filter: "blur(2px)" }
              }
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              <motion.div
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="flex flex-col items-center gap-3"
                initial={skipEntry ? false : { opacity: 0, y: 16, scale: 0.92 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              >
                <LogoIcon className="logo-foil-soft" foil size="xl" />
                <h1>
                  <Logotype glow />
                </h1>
              </motion.div>

              <div className="pointer-events-auto flex w-full flex-col items-center gap-10">
                <AnimatePresence mode="wait">
                  {!showRecover ? (
                    <motion.div
                      key="pin"
                      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                      className="flex flex-col items-center gap-10"
                      exit={{
                        opacity: 0,
                        y: -8,
                        filter: "blur(2px)",
                        transition: { duration: 0.15, ease: "easeIn" },
                      }}
                      initial={
                        skipEntry
                          ? false
                          : { opacity: 0, y: 12, filter: "blur(2px)" }
                      }
                      transition={{
                        duration: 0.3,
                        ease: [0.16, 1, 0.3, 1],
                        delay: 0.05,
                      }}
                    >
                      <div className="flex w-full flex-col items-center gap-3">
                        <Link
                          className="group inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full border border-cyan-200/25 bg-cyan-300/10 px-6 py-3 text-sm font-medium text-cyan-100 transition-colors hover:border-cyan-200/45 hover:bg-cyan-300/15 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-200"
                          href="/purchase"
                        >
                          {te("Sign up free · 2.0 General")}
                          <ArrowRight
                            className="transition-transform duration-200 group-hover:translate-x-0.5"
                            size={16}
                          />
                        </Link>
                      </div>
                      <div className="flex flex-col items-center gap-4">
                        <p className="text-xs text-white/50">
                          {te("Already have an account? Log in with your PIN")}
                        </p>
                        <PinInput
                          error={errorKind !== null}
                          loading={loading}
                          resetKey={resetKey}
                          onErrorClear={() => setErrorKind(null)}
                          onSubmit={submit}
                        />
                      </div>
                      {errorMessage ? (
                        <p className="text-faint -mt-6 text-center text-xs">
                          {errorMessage}
                        </p>
                      ) : null}
                      <motion.div
                        animate={{ opacity: 1 }}
                        className="flex flex-col items-center gap-3"
                        initial={skipEntry ? false : { opacity: 0 }}
                        transition={{ duration: 0.4, delay: 0.35 }}
                      >
                        <button
                          className="cursor-pointer text-[13px] font-medium tracking-[-0.01em] text-white/52 transition-colors hover:text-white/82"
                          onClick={() => setShowRecover(true)}
                        >
                          {te("Forgot your PIN?")}
                        </button>
                      </motion.div>
                    </motion.div>
                  ) : (
                    <RecoveryForm
                      challenge={challenge}
                      error={
                        recoverError === "rate_limited"
                          ? te(
                              "Too many email requests. Please wait before trying again.",
                            )
                          : recoverError
                            ? te(
                                "Unable to send your PIN. Please retry the verification.",
                              )
                            : undefined
                      }
                      email={recoverEmail}
                      loading={recoverLoading}
                      sent={recoverSent}
                      onEmailChange={setRecoverEmail}
                      onSubmit={handleRecover}
                      onBack={() => {
                        cancelVerification();
                        setShowRecover(false);
                        setRecoverSent(false);
                        setRecoverEmail("");
                      }}
                    />
                  )}
                </AnimatePresence>

                <motion.p
                  animate={{ opacity: 1 }}
                  className="max-w-sm text-center text-[11px] leading-5 tracking-[-0.01em] text-white/24"
                  initial={skipEntry ? false : { opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.45 }}
                >
                  {te.rich(
                    "By logging in, you agree to our <terms>Terms</terms> and <privacy>Privacy Policy</privacy>.",
                    {
                      terms: (chunks) => (
                        <Link
                          className="text-white/44 underline decoration-white/16 underline-offset-4 transition-colors hover:text-white/72"
                          href="/terms"
                        >
                          {chunks}
                        </Link>
                      ),
                      privacy: (chunks) => (
                        <Link
                          className="text-white/44 underline decoration-white/16 underline-offset-4 transition-colors hover:text-white/72"
                          href="/privacy"
                        >
                          {chunks}
                        </Link>
                      ),
                    },
                  )}
                </motion.p>

                <motion.div
                  animate={{ opacity: 1 }}
                  className="-mt-6"
                  initial={skipEntry ? false : { opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <Link
                    className="group inline-flex items-center gap-1.5 text-[11px] font-medium tracking-[-0.005em] text-white/34 transition-colors hover:text-cyan-50/72"
                    href={import.meta.env.DEV ? "/?marketing-preview=1" : "/"}
                  >
                    {te("Official website")}
                    <ArrowRight
                      className="transition-transform duration-200 group-hover:translate-x-0.5"
                      size={11}
                    />
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </UnauthShell>
  );
}

function LoginWorkspaceGateway({
  onEnter,
  canEnterFlagship,
}: {
  onEnter: (target: "general" | "flagship") => void;
  canEnterFlagship: boolean;
}) {
  const te = useExtracted();
  const cards: Array<{
    target: "general" | "flagship";
    title: string;
    badge: string;
    action: string;
    icon: LucideIcon;
    bandLabel: string;
  }> = [
    {
      target: "general",
      title: te("2.0"),
      badge: te("Free"),
      action: te("Enter 2.0"),
      icon: LayoutGrid,
      bandLabel: te("General Edition"),
    },
    {
      target: "flagship",
      title: te("3.0"),
      badge: te("Subscription"),
      action: canEnterFlagship ? te("Enter 3.0") : te("Discover Flagship"),
      icon: Layers3,
      bandLabel: te("Flagship Edition"),
    },
  ];

  return (
    <motion.div
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      className="pointer-events-auto flex w-full justify-center px-4 pb-8 pt-4 sm:px-6 sm:pt-6"
      exit={{
        opacity: 0,
        y: -8,
        filter: "blur(4px)",
        transition: { duration: 0.2, ease: "easeIn" },
      }}
      initial={{ opacity: 0, y: 16, filter: "blur(5px)" }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1], delay: 0.12 }}
    >
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 pb-16 sm:gap-5 sm:pb-10">
        <div className="relative w-full overflow-hidden rounded-[20px] border border-white/7 bg-[linear-gradient(180deg,rgba(22,28,42,0.42),rgba(18,22,34,0.24)_100%)] px-5 py-3 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_10px_26px_rgba(0,0,0,0.12)] backdrop-blur-[14px] sm:rounded-[24px]">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(100,228,255,0.07)_34%,rgba(171,108,255,0.07)_68%,transparent_100%)] opacity-70" />
          <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-[linear-gradient(90deg,transparent,rgba(160,237,255,0.2),transparent)]" />
          <div className="relative inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.18em] uppercase text-white/66">
            <CheckCircle2
              className="text-white/52"
              size={13}
              strokeWidth={2.4}
            />
            {te("Login successful!")}
          </div>
          <p className="relative mx-auto mt-1.5 max-w-md text-sm leading-5 text-white/44">
            <span className="block">{te("Your workspace is ready.")}</span>
            <span className="block">
              {te("Choose where you want to enter.")}
            </span>
          </p>
        </div>

        <section className="grid w-full grid-cols-1 gap-3 sm:gap-4">
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <button
                key={card.target}
                className="group relative overflow-hidden rounded-[20px] border border-white/12 bg-[linear-gradient(180deg,rgba(38,45,63,0.9),rgba(30,35,51,0.9)_44%,rgba(23,27,40,0.92)_100%)] p-3 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_18px_38px_rgba(0,0,0,0.24)] transition-all duration-[420ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:border-white/16 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_22px_44px_rgba(0,0,0,0.28)] sm:rounded-[24px] sm:p-4"
                type="button"
                onClick={() => onEnter(card.target)}
              >
                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.02)_0%,transparent_26%),linear-gradient(90deg,transparent_0%,rgba(100,228,255,0.26)_36%,rgba(171,108,255,0.22)_68%,transparent_100%)] opacity-70" />
                <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-[linear-gradient(90deg,transparent,rgba(160,237,255,0.62),transparent)]" />
                <div className="pointer-events-none absolute left-[14%] right-[10%] top-[44%] h-[18%] rounded-full bg-[linear-gradient(90deg,transparent_0%,rgba(99,227,255,0.36)_34%,rgba(175,106,255,0.26)_70%,transparent_100%)] opacity-90 blur-[0.5px]" />

                <div className="relative flex min-h-[104px] flex-col sm:min-h-[128px]">
                  <div className="flex items-center gap-2.5">
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-white/82 sm:size-10">
                      <Icon size={16} />
                    </span>
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <h2 className="text-lg font-semibold tracking-[-0.03em] text-white sm:text-xl">
                        {card.title}
                      </h2>
                      <span className="shrink-0 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-medium text-white/65 sm:text-xs">
                        {card.badge}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-white/78 sm:text-sm">
                      <span>{card.action}</span>
                      <ArrowRight
                        className="text-white/34 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-white/72"
                        size={14}
                      />
                    </div>
                  </div>

                  <div className="pointer-events-none absolute inset-x-[12%] top-[58%] flex -translate-y-1/2 items-center justify-center">
                    <span className="text-center text-[11px] font-medium tracking-[0.12em] uppercase text-white/64 sm:text-xs">
                      {card.bandLabel}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </section>
      </div>
    </motion.div>
  );
}
